import type { IIndex } from "../types/generic";

export interface IContentReplacement {
  [key: string]: string | number;
}

/**
 * Replaces placeholders in CMS content with dynamic values
 * @param content - The CMS content string that may contain placeholders like {reportOnlyLimit}
 * @param replacements - Object containing the replacement values
 * @returns The content with placeholders replaced by actual values
 */
export function replaceContentPlaceholders(
  content: string,
  replacements: IContentReplacement
): string {
  if (!content || typeof content !== "string") {
    return content;
  }

  let result = content;

  // Replace each placeholder with its corresponding value
  Object.entries(replacements).forEach(([key, value]) => {
    const placeholder = `{${key}}`;
    const regex = new RegExp(
      placeholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
      "g"
    );
    result = result.replace(regex, String(value));
  });

  return result;
}

/**
 * Processes all content in an info object, replacing placeholders with dynamic values
 * @param info - The info object containing CMS content
 * @param replacements - Object containing the replacement values
 * @returns The info object with all placeholders replaced
 */
export function processInfoContent(
  info: IIndex,
  replacements: IContentReplacement
): IIndex {
  if (!info || typeof info !== "object") {
    return info;
  }

  const processedInfo = { ...info };

  // Process each string value in the info object
  Object.keys(processedInfo).forEach((key) => {
    const value = processedInfo[key];
    if (typeof value === "string") {
      processedInfo[key] = replaceContentPlaceholders(value, replacements);
    }
  });

  return processedInfo;
}
